import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCheck, ChevronDown, Copy, Layers, MoreVertical, Search, TriangleAlert } from "lucide-react";
import { cn } from "../../../utils/function";
import {
  autoMap,
  CONVOHUB_BREAKDOWN,
  enrichmentCoverage,
  fieldStatus,
  FREQUENCIES,
  mappingCounts,
  MapStatus,
  nextRefresh,
  PROFILE_FIELDS,
  selectedRecords,
  SOURCE_FIELDS,
  SourceIconKey,
  SOURCES,
  updateUnifySetup,
  UnifySetup,
  useUnifySetup,
} from "./data";
import { card, fmt, linkCls, PageHeader, Pill, primaryBtn, secondaryBtn, SourceIcon, StatTile, Stepper } from "./ui";

// Setup Baseline Profiles (/unify/setup): Select Data Sources → Map Fields
// → Configure Refresh → Review and Start, with a running Setup Summary.
// Choices persist in the browser (data.ts) until there's a backend.

const STEPS = ["Select Data Sources", "Map Fields", "Configure Refresh", "Review and Start"];

const selectCls =
  "w-full appearance-none h-11 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 pl-3 pr-9 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-indigo-400";

const Select: React.FC<{ value: string; options: string[]; placeholder: string; onChange: (v: string) => void; muted?: boolean }> = ({ value, options, placeholder, onChange, muted }) => (
  <div className="relative">
    <select className={cn(selectCls, !value && "text-gray-400", muted && "bg-gray-50")} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
    <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
  </div>
);

const SearchBox: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <div className="relative w-full sm:w-72">
    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search Sources or fields. . ."
      className="w-full h-11 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 pl-10 pr-3 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-indigo-400"
    />
  </div>
);

const STATUS_TONE: Record<MapStatus, "green" | "yellow" | "orange"> = { "Fully Mapped": "green", "Partially Mapped": "yellow", Unmapped: "orange" };

// ── Summary ─────────────────────────────────────────────────────────────────

const Summary: React.FC<{ s: UnifySetup }> = ({ s }) => {
  const counts = mappingCounts(s);
  const coverage = enrichmentCoverage(s);
  const chosen = SOURCES.filter((src) => s.selected.includes(src.id));
  return (
    <aside className="bg-white dark:bg-gray-900 border-t xl:border-t-0 xl:border-l border-gray-200 dark:border-gray-800 xl:w-[300px] shrink-0">
      <div className="bg-emerald-50 dark:bg-emerald-500/10 px-5 py-4 border-b border-gray-200 dark:border-gray-800">
        <h2 className="text-xl font-medium text-gray-900 dark:text-gray-100">Setup Summary</h2>
      </div>
      <div className="px-5 py-4 space-y-4 text-sm">
        <div>
          <p className="text-gray-700 dark:text-gray-300">Selected Records</p>
          <p className="text-2xl text-gray-900 dark:text-gray-100">{fmt(selectedRecords(s))}</p>
        </div>
        <div>
          <p className="text-gray-700 dark:text-gray-300">Selected Sources</p>
          <p className="text-2xl text-gray-900 dark:text-gray-100">{chosen.length}</p>
          <ul className="mt-2 space-y-2 max-h-56 overflow-y-auto pr-1">
            {chosen.map((src) => (
              <li key={src.id}>
                <p className="flex items-center gap-1.5 text-gray-800 dark:text-gray-200">
                  <SourceIcon id={src.id} size={14} /> {src.name}
                </p>
                <p className="text-gray-900 dark:text-gray-100">{fmt(src.records)} records</p>
                <p className="text-xs text-gray-500">Last Sync {src.lastSync}</p>
              </li>
            ))}
          </ul>
        </div>
        <div className="grid grid-cols-3 xl:grid-cols-1 gap-3">
          <div>
            <p className="text-gray-700 dark:text-gray-300">Fully Mapped</p>
            <p className="text-2xl text-emerald-600">{counts.full}</p>
          </div>
          <div>
            <p className="text-gray-700 dark:text-gray-300">Partially Mapped</p>
            <p className="text-2xl text-yellow-500">{counts.partial}</p>
          </div>
          <div>
            <p className="text-gray-700 dark:text-gray-300">Unmapped</p>
            <p className="text-2xl text-rose-600 underline underline-offset-4">{counts.unmapped}</p>
          </div>
        </div>
        <div>
          <p className="text-gray-700 dark:text-gray-300">Enrichment Coverage</p>
          <p className="text-xs text-gray-900 dark:text-gray-100 mt-1">{coverage}%</p>
          <div className="mt-1.5 h-1 rounded-full bg-gray-100 dark:bg-gray-800">
            <div className="h-1 rounded-full bg-emerald-500 transition-all" style={{ width: `${coverage}%` }} />
          </div>
        </div>
      </div>
    </aside>
  );
};

// ── Step 1 ──────────────────────────────────────────────────────────────────

const SelectSources: React.FC<{ s: UnifySetup }> = ({ s }) => {
  const [convohub, ...others] = SOURCES;
  const toggle = (id: SourceIconKey) =>
    updateUnifySetup({ selected: s.selected.includes(id) ? s.selected.filter((x) => x !== id) : [...s.selected, id] });
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl text-gray-900 dark:text-gray-100">Data Sources</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Select data sources to include in your baseline profile. Combine multiple sources to create a richer, more complete view of your customers
        </p>
      </div>
      <div className={cn(card, "p-4 sm:p-5")}>
        <div className="flex gap-3">
          <SourceIcon id="convohub" size={28} />
          <div className="flex-1 min-w-0">
            <p className="flex flex-wrap items-center gap-2 text-lg sm:text-xl text-gray-900 dark:text-gray-100">
              {convohub.name}
              <Pill tone="green" dot>
                Included by default
              </Pill>
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{convohub.description}</p>
            <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3 rounded-xl bg-stone-50 dark:bg-gray-800/60 p-4">
              {CONVOHUB_BREAKDOWN.map((b) => (
                <div key={b.label}>
                  <p className="text-lg text-gray-900 dark:text-gray-100">{fmt(b.value)}</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{b.label}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-sm text-gray-500">Last Sync {convohub.lastSync}</p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-xl sm:text-2xl text-gray-900 dark:text-gray-100">Other Connected Business Sources</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Add data from your connected business tools to enrich profiles and improve identity matching</p>
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 mt-4">
          {others.map((src) => {
            const on = s.selected.includes(src.id);
            return (
              <label key={src.id} className={cn(card, "p-4 sm:p-5 flex gap-3 cursor-pointer transition-colors", on ? "border-indigo-300 dark:border-indigo-500/50" : "hover:border-gray-300")}>
                <SourceIcon id={src.id} size={26} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-lg text-gray-900 dark:text-gray-100">{src.name}</p>
                    <input type="checkbox" checked={on} onChange={() => toggle(src.id)} className="w-5 h-5 mt-1 accent-indigo-700 shrink-0" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{src.description}</p>
                  <div className="mt-3 rounded-xl bg-stone-50 dark:bg-gray-800/60 p-4">
                    <p className="text-lg text-gray-900 dark:text-gray-100">{fmt(src.records)}</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">available records</p>
                  </div>
                  <p className="mt-3 text-sm text-gray-500">Last Sync {src.lastSync}</p>
                </div>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Step 2 ──────────────────────────────────────────────────────────────────

const MapFields: React.FC<{ s: UnifySetup }> = ({ s }) => {
  const chosen = SOURCES.filter((src) => s.selected.includes(src.id));
  const [active, setActive] = useState<SourceIconKey>(chosen[0]?.id || "convohub");
  const [q, setQ] = useState("");
  const [fieldFilter, setFieldFilter] = useState("All Profile Fields");
  const [statusFilter, setStatusFilter] = useState("All");
  const map = s.mapping[active] || {};

  const setField = (fieldId: string, value: string) =>
    updateUnifySetup({ mapping: { ...s.mapping, [active]: { ...map, [fieldId]: value } } });

  const autoMapAll = () => {
    const next = { ...s.mapping };
    chosen.forEach((src) => (next[src.id] = { ...autoMap(src.id), ...next[src.id] }));
    updateUnifySetup({ mapping: next });
    toast.success("Fields auto-mapped across all selected sources");
  };

  const query = q.trim().toLowerCase();
  const sources = chosen.filter((src) => !query || src.name.toLowerCase().includes(query) || PROFILE_FIELDS.some((f) => f.label.toLowerCase().includes(query)));
  const rows = PROFILE_FIELDS.filter(
    (f) =>
      (fieldFilter === "All Profile Fields" || (fieldFilter === "Identifiers only" ? ["customer_id", "email", "phone"].includes(f.id) : true)) &&
      (statusFilter === "All" || (statusFilter === "Mapped" ? !!map[f.id] : !map[f.id])) &&
      (!query || f.label.toLowerCase().includes(query) || chosen.some((src) => src.name.toLowerCase().includes(query)))
  );

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl sm:text-2xl text-gray-900 dark:text-gray-100">Map Selected Fields</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Select the corresponding field from each source for every unified profile field</p>
      </div>
      <div className={cn(card, "p-4")}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <SearchBox value={q} onChange={setQ} />
          <div className="flex flex-wrap gap-2 lg:ml-auto">
            <div className="w-44">
              <Select value={fieldFilter} options={["Identifiers only"]} placeholder="All Profile Fields" onChange={(v) => setFieldFilter(v || "All Profile Fields")} />
            </div>
            <div className="w-48">
              <Select value={statusFilter === "All" ? "" : statusFilter} options={["Mapped", "Unmapped"]} placeholder="Field Mapping: All" onChange={(v) => setStatusFilter(v || "All")} />
            </div>
            <button type="button" onClick={autoMapAll} className={primaryBtn}>
              Auto Map Fields
            </button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
          <div>
            <p className="text-sm text-gray-800 dark:text-gray-200 mb-2">Sources</p>
            <ul className="flex lg:flex-col gap-2 lg:gap-0 overflow-x-auto lg:divide-y divide-gray-200 dark:divide-gray-800">
              {sources.map((src) => (
                <li key={src.id} className="shrink-0">
                  <button
                    type="button"
                    onClick={() => setActive(src.id)}
                    className={cn("w-full flex items-start gap-2 px-3 py-3 rounded-lg text-left", active === src.id ? "bg-indigo-50 dark:bg-indigo-500/15" : "hover:bg-gray-50 dark:hover:bg-gray-800")}
                  >
                    <SourceIcon id={src.id} size={18} />
                    <span>
                      <span className="block text-[15px] text-gray-900 dark:text-gray-100 whitespace-nowrap">{src.name}</span>
                      <span className="block text-xs text-gray-600 dark:text-gray-400">{fmt(src.records)} records</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="text-left text-gray-800 dark:text-gray-200">
                  <th className="font-normal pb-3 pr-3">Baseline Profile Field</th>
                  <th className="font-normal pb-3 pr-3">Source Field</th>
                  <th className="font-normal pb-3 pr-3">Type/ Definition</th>
                  <th className="font-normal pb-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id}>
                    <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{f.label}</td>
                    <td className="py-2 pr-3 w-60">
                      <Select value={map[f.id] || ""} options={SOURCE_FIELDS[active]} placeholder="Unmapped" muted={!map[f.id]} onChange={(v) => setField(f.id, v)} />
                    </td>
                    <td className="py-2 pr-3">
                      <span className="text-xs bg-indigo-50 dark:bg-indigo-500/15 text-gray-700 dark:text-gray-300 rounded px-1.5 py-1">{f.type}</span>
                    </td>
                    <td className="py-2 text-right">
                      <button type="button" aria-label={`Clear ${f.label}`} title="Clear mapping" onClick={() => setField(f.id, "")} className="p-1 text-indigo-700 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                        <MoreVertical size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <p className="py-8 text-center text-sm text-gray-500">No fields match these filters.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Step 3 ──────────────────────────────────────────────────────────────────

const ConfigureRefresh: React.FC<{ s: UnifySetup }> = ({ s }) => {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const chosen = SOURCES.filter((src) => s.selected.includes(src.id));
  const rows = chosen.filter(
    (src) => (!q.trim() || src.name.toLowerCase().includes(q.trim().toLowerCase())) && (!filter || (filter === "Not Set" ? !s.frequency[src.id] : !!s.frequency[src.id]))
  );
  const setFreq = (id: SourceIconKey, v: string) => updateUnifySetup({ frequency: { ...s.frequency, [id]: v } });
  const applyAll = (v: string) => updateUnifySetup({ frequency: Object.fromEntries(chosen.map((src) => [src.id, v])) });

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl sm:text-2xl text-gray-900 dark:text-gray-100">3. Configure Refresh Frequency</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Choose how often each connected source should refresh your customer profiles</p>
      </div>
      <div className={cn(card, "p-4")}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <SearchBox value={q} onChange={setQ} />
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <div className="w-52">
              <Select value={filter} options={["Not Set", "Set"]} placeholder="Frequency: All" onChange={setFilter} />
            </div>
            <div className="w-44">
              <Select value="" options={FREQUENCIES} placeholder="Apply to all…" onChange={(v) => v && applyAll(v)} />
            </div>
          </div>
        </div>
        <div className="overflow-x-auto mt-5">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-left text-gray-800 dark:text-gray-200">
                <th className="font-normal pb-3 pr-3">Sources</th>
                <th className="font-normal pb-3 pr-3">Refresh Frequency</th>
                <th className="font-normal pb-3 pr-3">Last Refresh</th>
                <th className="font-normal pb-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((src) => {
                const f = s.frequency[src.id] || "";
                return (
                  <tr key={src.id} className="align-top">
                    <td className="py-3 pr-3">
                      <p className="flex items-center gap-2 text-[15px] text-gray-900 dark:text-gray-100">
                        <SourceIcon id={src.id} size={18} /> {src.name}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 ml-7">{fmt(src.records)} records</p>
                    </td>
                    <td className="py-3 pr-3 w-64">
                      <Select value={f} options={FREQUENCIES} placeholder="Set Frequency" onChange={(v) => setFreq(src.id, v)} />
                      {f && <p className="mt-1 text-xs text-gray-500">Next Refresh: {nextRefresh(f)}</p>}
                    </td>
                    <td className={cn("py-3 pr-3 pt-5 text-xs sm:text-sm whitespace-nowrap", f ? "text-emerald-600" : "text-rose-500")}>{f ? src.lastSync : "Frequency Not set"}</td>
                    <td className="py-3 pt-4 text-right">
                      <button type="button" aria-label="Clear frequency" title="Clear frequency" onClick={() => setFreq(src.id, "")} className="p-1 text-indigo-700 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                        <MoreVertical size={18} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Step 4 ──────────────────────────────────────────────────────────────────

const Review: React.FC<{ s: UnifySetup; onEdit: (step: number) => void }> = ({ s, onEdit }) => {
  const navigate = useNavigate();
  const chosen = SOURCES.filter((src) => s.selected.includes(src.id));
  const counts = mappingCounts(s);
  const matchRate = useMemo(() => (counts.full * 100 + counts.partial * 60) / Math.max(1, PROFILE_FIELDS.length), [counts.full, counts.partial]);
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl text-gray-900 dark:text-gray-100">Review and Start Unification</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Review your selection and generate baseline profiles</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-3">
        <StatTile icon={<Copy size={18} className="text-indigo-600" />} label="Connected Sources" value={chosen.length} description="Active data sources connected to ConvoHub" footer={<button type="button" onClick={() => onEdit(0)} className={linkCls}>View Connectors</button>} />
        <StatTile icon={<Layers size={18} className="text-indigo-600" />} label="Total Records" value={fmt(selectedRecords(s))} description="Active records from all selected data sources" />
        <StatTile icon={<CheckCheck size={18} className="text-indigo-600" />} label="Fully Mapped" value={counts.full} description="Profile fields mapped in every selected source" footer={<button type="button" onClick={() => onEdit(1)} className={linkCls}>View Mapping</button>} />
        <StatTile icon={<AlertTriangle size={18} className="text-yellow-500" />} labelClass="text-yellow-500" valueClass="text-yellow-500" label="Partially Mapped" value={counts.partial} description="Fields mapped in only some sources" footer={<button type="button" onClick={() => onEdit(1)} className={linkCls}>Review Records</button>} />
        <StatTile icon={<TriangleAlert size={18} className="text-orange-500" />} labelClass="text-orange-500" valueClass="text-orange-500" label="Unmapped" value={counts.unmapped} description="Fields not mapped in any source" footer={<button type="button" onClick={() => onEdit(1)} className={linkCls}>Review Records</button>} />
        <StatTile icon={<Layers size={18} className="text-indigo-600" />} label="Match Rate" value={`${matchRate.toFixed(2)}%`} valueClass="text-emerald-600" description="Estimated share of records matched across sources" />
      </div>

      <div className={cn(card, "p-4")}>
        <h3 className="text-[15px] font-medium text-gray-900 dark:text-gray-100">Field Mapping Preview</h3>
        <p className="text-xs text-gray-600 dark:text-gray-400">Preview mapped fields</p>
        <div className="overflow-x-auto mt-3">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="text-left text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-800">
                <th className="font-medium py-3 pr-3">Unified Profile Field</th>
                {chosen.map((src) => (
                  <th key={src.id} className="font-medium py-3 pr-3 whitespace-nowrap">{src.name}</th>
                ))}
                <th className="font-medium py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {PROFILE_FIELDS.map((f) => {
                const status = fieldStatus(s, f.id);
                return (
                  <tr key={f.id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-4 pr-3 text-gray-800 dark:text-gray-200">
                      {f.label}
                      {f.primary && <span className="block w-fit mt-1 text-xs bg-emerald-200 text-emerald-900 rounded px-1.5 py-0.5">Primary Identifier</span>}
                    </td>
                    {chosen.map((src) => (
                      <td key={src.id} className={cn("py-4 pr-3", s.mapping[src.id]?.[f.id] ? "text-gray-900 dark:text-gray-100" : "text-gray-400")}>
                        {s.mapping[src.id]?.[f.id] || "—"}
                      </td>
                    ))}
                    <td className="py-4">
                      <Pill tone={STATUS_TONE[status]} dot>
                        {status}
                      </Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className={cn(card, "p-4")}>
        <h3 className="text-[15px] font-medium text-gray-900 dark:text-gray-100">Source Summary</h3>
        <p className="text-xs text-gray-600 dark:text-gray-400">All connected sources for generating baseline profiles</p>
        <div className="overflow-x-auto mt-3">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-left text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-800">
                <th className="font-medium py-3 pr-3">Source</th>
                <th className="font-medium py-3 pr-3">Records</th>
                <th className="font-medium py-3 pr-3">Selected Fields</th>
                <th className="font-medium py-3 pr-3">Status</th>
                <th className="font-medium py-3 pr-3">Refresh</th>
                <th className="font-medium py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {chosen.map((src) => (
                <tr key={src.id} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-4 pr-3">
                    <span className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                      <SourceIcon id={src.id} size={18} /> {src.name}
                    </span>
                  </td>
                  <td className="py-4 pr-3 text-gray-800 dark:text-gray-200 whitespace-nowrap">{fmt(src.records)} Records</td>
                  <td className="py-4 pr-3 text-gray-800 dark:text-gray-200">{Object.values(s.mapping[src.id] || {}).filter(Boolean).length}</td>
                  <td className="py-4 pr-3">
                    <Pill tone="green" dot>
                      Connected
                    </Pill>
                  </td>
                  <td className="py-4 pr-3 text-gray-800 dark:text-gray-200 whitespace-nowrap">{s.frequency[src.id] ? `Every ${s.frequency[src.id]}` : "No refresh runs"}</td>
                  <td className="py-4 text-right">
                    <button type="button" aria-label={`Edit ${src.name}`} title="Edit mapping" onClick={() => onEdit(1)} className="p-1 text-indigo-700 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                      <MoreVertical size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Generating creates baseline profiles from the sources above.{" "}
        <button type="button" onClick={() => navigate("/unify/merge-logs")} className={linkCls}>
          See how records are merged
        </button>
      </p>
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────

const UnifySetupPage: React.FC = () => {
  const s = useUnifySetup();
  const navigate = useNavigate();
  const step = Math.min(s.step, STEPS.length - 1);
  const setStep = (n: number) => {
    updateUnifySetup({ step: n });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const problem = (from: number): string | null => {
    if (from === 0 && s.selected.length === 0) return "Select at least one data source.";
    if (from === 1 && !s.selected.some((src) => s.mapping[src]?.phone)) return "Map the Phone Number (primary identifier) for at least one source.";
    if (from === 2 && s.selected.some((src) => !s.frequency[src])) return "Set a refresh frequency for every source.";
    return null;
  };

  const goTo = (target: number) => {
    for (let i = step; i < target; i++) {
      const p = problem(i);
      if (p) {
        setStep(i);
        return toast.error(p);
      }
    }
    setStep(target);
  };

  const generate = () => {
    updateUnifySetup({ generatedAt: new Date().toISOString(), step: 0 });
    toast.success("Unified profiles generated", { description: "Your baseline profiles are now live." });
    navigate("/unify/dashboard");
  };

  const footer = [
    <div key="0" className="flex flex-wrap justify-end gap-3">
      <button type="button" onClick={() => toast.success("Progress saved")} className={secondaryBtn}>Save Progress</button>
      <button type="button" onClick={() => goTo(1)} className={primaryBtn}>Continue to Mapping</button>
    </div>,
    <div key="1" className="flex flex-wrap justify-end gap-3">
      <button type="button" onClick={() => setStep(0)} className={secondaryBtn}>Back</button>
      <button type="button" onClick={() => goTo(2)} className={primaryBtn}>Configure Refresh</button>
    </div>,
    <div key="2" className="flex flex-wrap justify-end gap-3">
      <button type="button" onClick={() => setStep(1)} className={secondaryBtn}>Back</button>
      <button type="button" onClick={() => goTo(3)} className={primaryBtn}>Review</button>
    </div>,
    <div key="3" className="flex flex-wrap justify-end gap-3">
      <button type="button" onClick={() => setStep(1)} className={secondaryBtn}>Back to Mapping</button>
      <button type="button" onClick={generate} className={primaryBtn}>Generate Unified Profiles</button>
    </div>,
  ][step];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <PageHeader crumbs={["Unify", "Setup Baseline Profiles"]} back="/unify/dashboard">
        <Stepper steps={STEPS} step={step} onStep={goTo} />
      </PageHeader>
      <div className="flex flex-col xl:flex-row bg-gray-100/80 dark:bg-gray-900/40 border border-t-0 border-gray-200 dark:border-gray-800 rounded-b-xl overflow-hidden">
        <div className="flex-1 min-w-0 px-4 sm:px-6 py-6 space-y-6">
          {step === 0 && <SelectSources s={s} />}
          {step === 1 && <MapFields s={s} />}
          {step === 2 && <ConfigureRefresh s={s} />}
          {step === 3 && <Review s={s} onEdit={setStep} />}
          {footer}
        </div>
        {step < 3 && <Summary s={s} />}
      </div>
    </div>
  );
};

export default UnifySetupPage;
